// Email verification form component for Cognito registration
// Requirements: 8.2

import React, { useState } from 'react';
import { AlertTriangleIcon } from '../common/icons';
import { CognitoAuthService } from '../../services/cognitoAuthService';
import './EmailVerificationForm.css';

interface EmailVerificationFormProps {
  email: string;
  onVerificationSuccess: () => void;
  onBackToLogin: () => void;
}

export const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({
  email,
  onVerificationSuccess,
  onBackToLogin
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || !verificationCode.trim()) return;

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await CognitoAuthService.confirmRegistration(email, verificationCode.trim());
      setSuccessMessage('Email verified successfully! You can now sign in.');
      
      // Wait a moment to show success message, then redirect
      setTimeout(() => {
        onVerificationSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Email verification error:', err);
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (isResending) return;

    setIsResending(true);
    setError('');
    setSuccessMessage('');

    try {
      await CognitoAuthService.resendConfirmationCode(email);
      setSuccessMessage('Verification code sent! Please check your email.');
    } catch (err: any) {
      console.error('Resend code error:', err);
      setError(err.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleInputChange = (value: string) => {
    // Only allow digits and limit to 6 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(cleanValue);
    
    if (error) {
      setError('');
    }
  };

  const isFormValid = verificationCode.trim().length === 6;

  return (
    <div className="email-verification-form">
      <div className="email-verification-form__header">
        <h2>Verify Your Email</h2>
        <p>
          We've sent a verification code to <strong>{email}</strong>. 
          Please enter the 6-digit code below to complete your registration.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="email-verification-form__form">
        {error && (
          <div className="email-verification-form__error" role="alert">
            <AlertTriangleIcon width={16} height={16} className="email-verification-form__error-icon" />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="email-verification-form__success" role="alert">
            {successMessage}
          </div>
        )}

        <div className="email-verification-form__field">
          <label htmlFor="verificationCode" className="email-verification-form__label">
            Verification Code
          </label>
          <input
            id="verificationCode"
            type="text"
            value={verificationCode}
            onChange={(e) => handleInputChange(e.target.value)}
            className="email-verification-form__input email-verification-form__input--code"
            placeholder="000000"
            disabled={isSubmitting}
            maxLength={6}
            autoComplete="one-time-code"
            required
          />
          <p className="email-verification-form__help-text">
            Enter the 6-digit code from your email
          </p>
        </div>

        <button
          type="submit"
          className={`email-verification-form__submit ${
            !isFormValid || isSubmitting ? 'email-verification-form__submit--disabled' : ''
          }`}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="email-verification-form__spinner"></span>
              Verifying...
            </>
          ) : (
            'Verify Email'
          )}
        </button>

        <div className="email-verification-form__actions">
          <button
            type="button"
            className="email-verification-form__link"
            onClick={handleResendCode}
            disabled={isResending || isSubmitting}
          >
            {isResending ? 'Sending...' : 'Resend verification code'}
          </button>

          <button
            type="button"
            className="email-verification-form__link"
            onClick={onBackToLogin}
            disabled={isSubmitting}
          >
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailVerificationForm;